import { useCallback, useEffect, useState } from 'react';
import { APP_VERSION } from '../config/appVersion';
import {
  evaluateAndroidUpdateOffer,
  type AndroidUpdateOffer,
  type AndroidVersionManifest,
} from '../core/config/androidUpdate';
import { isAndroidNativeRuntime } from '../adapters/mobile/apkInstaller';
import { useAndroidApkDownload } from './useAndroidApkDownload';
import {
  downloadAndInstallAndroidUpdate,
  fetchAndroidVersionManifest,
  type AndroidUpdateProgress,
} from '../services/mobile/androidAppUpdate';
import { readInstalledAndroidVersion } from '../services/mobile/androidInstalledVersion';
import { useRemoteConfig } from './useRemoteConfig';

export type HelpAboutAndroidPhase =
  | 'idle'
  | 'preparing'
  | 'downloading'
  | 'installing'
  | 'error';

/**
 * About sheet: live platform version from Storage + native update / web download.
 */
export function useHelpAboutAndroid() {
  const native = isAndroidNativeRuntime();
  const remoteConfig = useRemoteConfig();
  const { busy: downloadBusy, error: downloadError, download } = useAndroidApkDownload();
  const [manifest, setManifest] = useState<AndroidVersionManifest | null>(null);
  const [offer, setOffer] = useState<AndroidUpdateOffer | null>(null);
  const [installedName, setInstalledName] = useState(APP_VERSION);
  const [phase, setPhase] = useState<HelpAboutAndroidPhase>('idle');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const remote = await fetchAndroidVersionManifest();
        if (cancelled) return;
        setManifest(remote);
        if (!native) return;
        const installed = await readInstalledAndroidVersion();
        if (cancelled) return;
        setInstalledName(installed.versionName);
        if (!remote) return;
        const next = evaluateAndroidUpdateOffer(installed, remote, {
          minAppVersion: remoteConfig.minAppVersion,
        });
        setOffer(next.available ? next : null);
      } catch {
        if (!cancelled) setManifest(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [native, remoteConfig.minAppVersion]);

  const platformVersion = manifest?.versionName || APP_VERSION;
  const updateAvailable = Boolean(native && offer?.available);
  const busy = downloadBusy
    || phase === 'preparing'
    || phase === 'downloading'
    || phase === 'installing';

  const runPrimaryAction = useCallback(async () => {
    setErrorKey(null);
    if (updateAvailable && offer) {
      const mapPhase = (progress: AndroidUpdateProgress) => {
        if (progress.phase === 'preparing') setPhase('preparing');
        if (progress.phase === 'downloading') setPhase('downloading');
        if (progress.phase === 'installing') setPhase('installing');
        if (progress.phase === 'error') setPhase('error');
        if (progress.phase === 'done') setPhase('idle');
      };
      const result = await downloadAndInstallAndroidUpdate(offer, mapPhase);
      if (!result.ok) {
        setPhase('error');
        setErrorKey(result.reason ?? 'install_failed');
        return false;
      }
      setPhase('installing');
      return true;
    }
    return download();
  }, [download, offer, updateAvailable]);

  return {
    native,
    platformVersion,
    installedName,
    updateAvailable,
    busy,
    errorKey,
    downloadError,
    runPrimaryAction,
  };
}
